import type { AffiliateReportFilterOption } from '@/@types/AffiliateReport';
import { endOfDay, isValid, startOfDay } from 'date-fns';
import type { DateRange } from 'react-day-picker';

export interface ReportNavigationFilters {
  dateRange?: DateRange;
  role?: 'producer' | 'affiliate' | 'coproducer';
  products?: AffiliateReportFilterOption[];
  offers?: AffiliateReportFilterOption[];
  checkouts?: AffiliateReportFilterOption[];
  affiliates?: AffiliateReportFilterOption[];
}

function normalizeDateRange(dateRange?: DateRange): { from: Date; to: Date } {
  const from =
    dateRange?.from && isValid(dateRange.from)
      ? startOfDay(dateRange.from)
      : startOfDay(new Date());

  const to =
    dateRange?.to && isValid(dateRange.to)
      ? endOfDay(dateRange.to)
      : endOfDay(from);

  return { from, to };
}

function appendLabelValueArray(
  params: URLSearchParams,
  key: string,
  items?: AffiliateReportFilterOption[],
) {
  items?.forEach((item) => {
    if (!item?.value) return;
    const label = item.label || item.value;
    params.append(key, `${label}:::${item.value}`);
  });
}

function appendSharedReportParams(
  params: URLSearchParams,
  filters: ReportNavigationFilters,
) {
  const { from, to } = normalizeDateRange(filters.dateRange);
  params.set('from', from.toISOString());
  params.set('to', to.toISOString());

  appendLabelValueArray(params, 'products', filters.products);
  appendLabelValueArray(params, 'offers', filters.offers);
  appendLabelValueArray(params, 'checkouts', filters.checkouts);
  appendLabelValueArray(params, 'affiliates', filters.affiliates);
}

export function buildAnalyticsPath(filters: ReportNavigationFilters): string {
  const params = new URLSearchParams();
  appendSharedReportParams(params, filters);
  params.set('role', filters.role ?? 'producer');
  return `/reports/analytics?${params.toString()}`;
}

export function buildUtmPanelPath(filters: ReportNavigationFilters): string {
  const params = new URLSearchParams();
  appendSharedReportParams(params, filters);
  return `/reports/utm-panel?${params.toString()}`;
}

export function affiliateToFilterOption(affiliate: {
  id: string;
  name: string;
}): AffiliateReportFilterOption {
  return { label: affiliate.name, value: affiliate.id };
}

export function productsToFilterOptions(
  products: { productId: string; productName: string }[],
): AffiliateReportFilterOption[] {
  return products.map((product) => ({
    label: product.productName,
    value: product.productId,
  }));
}
